export const LOGOUT_REASON_KEY = 'cfsf_logout_reason';
export const LOGOUT_REASON_INACTIVITY = 'inactivity';
export const REQUIRE_CREDENTIAL_REAUTH_KEY = 'cfsf_require_credential_reauth';
export const REQUIRE_CREDENTIAL_REAUTH_VALUE = '1';
export const POST_LOGIN_RETURN_PATH_KEY = 'cfsf_post_login_return_path';
export const SHARED_LAST_ACTIVITY_KEY = 'cfsf_last_activity_at';
export const INACTIVITY_LOGOUT_BROADCAST_KEY = 'cfsf_inactivity_logout_at';
export const INACTIVITY_LOGOUT_BROADCAST_MAX_AGE_MS = 60 * 1000;

function readLocalTimestamp(key) {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeLocalTimestamp(key, value) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, String(value));
    }
  } catch {
    // Storage can be unavailable (private mode, quota); inactivity tracking stays per-tab.
  }
}

function removeLocalItem(key) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

/** Latest user activity across all open tabs, or null. Future timestamps are ignored. */
export function readSharedLastActivity(now = Date.now()) {
  const value = readLocalTimestamp(SHARED_LAST_ACTIVITY_KEY);
  return value !== null && value <= now ? value : null;
}

export function writeSharedLastActivity(timestamp = Date.now()) {
  writeLocalTimestamp(SHARED_LAST_ACTIVITY_KEY, timestamp);
}

/** Tell other tabs that this tab is signing out due to inactivity. */
export function broadcastInactivityLogout(timestamp = Date.now()) {
  writeLocalTimestamp(INACTIVITY_LOGOUT_BROADCAST_KEY, timestamp);
}

export function clearInactivityLogoutBroadcast() {
  removeLocalItem(INACTIVITY_LOGOUT_BROADCAST_KEY);
}

export function hasRecentInactivityLogoutBroadcast(now = Date.now()) {
  const value = readLocalTimestamp(INACTIVITY_LOGOUT_BROADCAST_KEY);
  return value !== null && value <= now && now - value < INACTIVITY_LOGOUT_BROADCAST_MAX_AGE_MS;
}

function isSafeReturnPath(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/') && !pathname.startsWith('//');
}

/** Remember where to send the user after they sign back in (survives hard redirects). */
export function setPostLoginReturnPath(pathname) {
  if (typeof sessionStorage === 'undefined' || !isSafeReturnPath(pathname)) {
    return;
  }
  sessionStorage.setItem(POST_LOGIN_RETURN_PATH_KEY, pathname);
}

export function peekPostLoginReturnPath() {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const path = sessionStorage.getItem(POST_LOGIN_RETURN_PATH_KEY);
  return isSafeReturnPath(path) ? path : null;
}

export function consumePostLoginReturnPath() {
  const path = peekPostLoginReturnPath();
  if (path && typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(POST_LOGIN_RETURN_PATH_KEY);
  }
  return path;
}

export function clearPostLoginReturnPath() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(POST_LOGIN_RETURN_PATH_KEY);
}

export function clearLogoutReason() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(LOGOUT_REASON_KEY);
}
export function setLogoutReason(reason) {
  if (typeof sessionStorage === 'undefined' || !reason) {
    return;
  }
  sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
}

/** Returns a post-logout path when a reason was stored, and clears it. */
export function consumeLogoutRedirectPath() {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
  if (reason === LOGOUT_REASON_INACTIVITY) {
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return '/session-expired';
  }
  return null;
}

/** Peek without clearing — use when multiple guards may run in the same tick. */
export function peekLogoutRedirectPath() {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
  return reason === LOGOUT_REASON_INACTIVITY ? '/session-expired' : null;
}

/** Mark that the next sign-in must re-enter credentials (survives hard redirects). */
export function setRequireCredentialReauth() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(REQUIRE_CREDENTIAL_REAUTH_KEY, REQUIRE_CREDENTIAL_REAUTH_VALUE);
}

export function peekRequireCredentialReauth() {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(REQUIRE_CREDENTIAL_REAUTH_KEY) === REQUIRE_CREDENTIAL_REAUTH_VALUE;
}

export function clearRequireCredentialReauth() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(REQUIRE_CREDENTIAL_REAUTH_KEY);
}

/**
 * If another tab just signed out for inactivity, mark this tab the same way so it
 * routes to /session-expired instead of /login. Returns true when applied.
 */
export function applyInactivityLogoutBroadcast(now = Date.now()) {
  if (!hasRecentInactivityLogoutBroadcast(now)) {
    return false;
  }
  setLogoutReason(LOGOUT_REASON_INACTIVITY);
  setRequireCredentialReauth();
  return true;
}

/** Returns true when reauth was required, and clears the flag. */
export function consumeRequireCredentialReauth() {
  if (!peekRequireCredentialReauth()) {
    return false;
  }
  clearRequireCredentialReauth();
  return true;
}
